import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { ArrowLeft, Users, PenTool, FileText, BarChart3, CheckCircle } from "lucide-react";
import { useRef, useCallback, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import heroDashboard from "@/assets/hero-dashboard.webp";

/* ─── Background decorations ─── */
const GeoShapes = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div
      className="absolute top-[-20%] right-[-5%] w-[400px] h-[400px] md:w-[700px] md:h-[700px] rounded-full opacity-20 md:opacity-25"
      style={{
        background: "radial-gradient(circle, hsl(30 92% 45% / 0.35) 0%, transparent 70%)",
      }}
    />
    <div
      className="absolute bottom-[-15%] left-[-10%] w-[300px] h-[300px] md:w-[500px] md:h-[500px] rounded-full opacity-15 md:opacity-20"
      style={{
        background: "radial-gradient(circle, hsl(220 80% 60% / 0.25) 0%, transparent 70%)",
      }}
    />
  </div>
);

/* ─── Feature list items ─── */
const heroFeatures = [
  { icon: Users, label: "פורטל דיירים רב שפתי", color: "text-[hsl(var(--cap-teal))]" },
  { icon: PenTool, label: "חתימות דיגיטליות", color: "text-[hsl(var(--cap-purple))]" },
  { icon: FileText, label: "יצירת מסמכים בקליק", color: "text-[hsl(var(--cap-orange))]" },
  { icon: BarChart3, label: "דוחות בזמן אמת", color: "text-[hsl(var(--cap-emerald))]" },
  { icon: CheckCircle, label: "מעקב סטטוס חתימה", color: "text-accent" },
];

/* ─── Mobile dashboard image (no hooks) ─── */
const MobileDashboardImage = () => (
  <div className="relative flex items-center justify-center">
    <img
      src={heroDashboard}
      alt="מערכת OneStep לניהול פינוי בינוי והתחדשות עירונית"
      className="relative w-full max-w-[85vw]"
      width="1000"
      height="625"
      loading="eager"
      fetchPriority="high"
    />
  </div>
);

/* ─── Desktop dashboard image with 3D tilt ─── */
const DesktopDashboardImage = ({ mouseX, mouseY }: { mouseX: ReturnType<typeof useMotionValue<number>>; mouseY: ReturnType<typeof useMotionValue<number>> }) => {
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [5, -5]), { stiffness: 100, damping: 30 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-5, 5]), { stiffness: 100, damping: 30 });
  const glowX = useTransform(mouseX, [-0.5, 0.5], [20, 80]);
  const glowY = useTransform(mouseY, [-0.5, 0.5], [20, 80]);
  const glowBg = useTransform(
    [glowX, glowY],
    ([x, y]) => `radial-gradient(700px circle at ${x}% ${y}%, hsl(30 92% 50% / 0.12), transparent 55%)`
  );

  return (
    <div className="relative flex items-center justify-center" style={{ perspective: 1200 }}>
      <motion.div
        className="relative cursor-default"
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
      >
        <motion.div
          className="absolute -inset-10 pointer-events-none blur-3xl -z-10"
          style={{ background: glowBg }}
        />
        <div className="relative">
          <img
            src={heroDashboard}
            alt="מערכת OneStep לניהול פינוי בינוי והתחדשות עירונית"
            className="relative w-full max-w-[600px] lg:max-w-[750px] xl:max-w-[900px] 2xl:max-w-[1000px]"
            width="1000"
            height="625"
            sizes="(max-width: 1023px) 600px, (max-width: 1279px) 750px, (max-width: 1535px) 900px, 1000px"
            loading="eager"
            fetchPriority="high"
          />
        </div>
      </motion.div>
    </div>
  );
};

const HeroSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const rectRef = useRef<DOMRect | null>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const isMobile = useIsMobile();

  // Cache rect and update on resize only
  useEffect(() => {
    const updateRect = () => {
      if (sectionRef.current) {
        rectRef.current = sectionRef.current.getBoundingClientRect();
      }
    };
    updateRect();
    window.addEventListener("resize", updateRect, { passive: true });
    return () => window.removeEventListener("resize", updateRect);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = rectRef.current;
    if (!rect) return;
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  }, [mouseX, mouseY]);

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0);
    mouseY.set(0);
  }, [mouseX, mouseY]);

  return (
    <section
      ref={sectionRef}
      onMouseMove={isMobile ? undefined : handleMouseMove}
      onMouseLeave={isMobile ? undefined : handleMouseLeave}
      className="relative overflow-hidden min-h-[100vh] flex items-center"
    >
      {/* Background */}
      <div className="absolute inset-0 gradient-hero" />
      <GeoShapes />

      <div className="relative container z-10 py-16 md:py-20 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Right side: Text content (RTL) */}
          <div className="order-2 lg:order-1 text-center lg:text-right">
            {/* Main headline */}
            {isMobile ? (
              <h1 className="text-3xl font-black leading-tight text-primary-foreground mb-6">
                מערכת לניהול פינוי בינוי
                <br />
                <span className="text-gradient">המתקדמת בישראל</span>
              </h1>
            ) : (
              <motion.h1
                className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black leading-tight text-primary-foreground mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                מערכת לניהול פינוי בינוי
                <br />
                <span className="text-gradient">המתקדמת בישראל</span>
              </motion.h1>
            )}

            {/* Feature list */}
            <ul className="flex flex-col gap-3 mb-8 max-w-md mx-auto lg:mx-0">
              {heroFeatures.map((feature) => (
                <li
                  key={feature.label}
                  className="flex items-center gap-3 text-primary-foreground/80 text-base md:text-lg"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                    <feature.icon className={`h-4 w-4 ${feature.color}`} />
                  </div>
                  <span>{feature.label}</span>
                </li>
              ))}
            </ul>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <Link
                to="/pricing"
                className="group relative overflow-hidden gradient-accent px-7 py-3.5 rounded-xl text-base font-bold text-accent-foreground shadow-xl shadow-accent/25 hover:shadow-accent/40 transition-all hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto text-center"
              >
                <span className="relative z-10">לצפייה במסלולים</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              </Link>
              <a
                href="https://app.onestep.co.il/login"
                className="group flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-base font-medium border border-primary-foreground/20 text-primary-foreground/90 hover:bg-primary-foreground/5 hover:border-primary-foreground/30 transition-all w-full sm:w-auto"
              >
                כניסה למערכת
                <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-5 mt-8 text-sm text-primary-foreground/45">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                <span>ללא עמלת הקמה</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                <span>הקמה תוך דקות</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                <span>תמיכה מלאה</span>
              </div>
            </div>
          </div>

          {/* Left side: Dashboard image */}
          <div className="order-1 lg:order-2">
            {isMobile ? <MobileDashboardImage /> : <DesktopDashboardImage mouseX={mouseX} mouseY={mouseY} />}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
